import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useAuth } from "../../auth/AuthProvider";
import { getNiceFormatDateTimeString } from "../../helpers/time";
import airtable from "../../lib/airtables";
import { Message } from "../../lib/airtables/models";
import { messageFieldIds } from "../../lib/airtables/tables/Messages";
import { teacherFieldIds } from "../../lib/airtables/tables/Teachers";
import { setMessagesUnread } from "../../redux/app/actions";

const getMessageValue = (record: Message, fieldId: string) => {
  const rawValue = record?.[fieldId];
  if (Array.isArray(rawValue)) {
    return rawValue[0];
  }
  return rawValue;
};

export default function MessagesPage() {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const recipientEmail = user?.[teacherFieldIds.email];
    if (!recipientEmail) {
      setLoading(false);
      return;
    }

    setLoading(true);
    airtable.messages.getItemsForRecipientEmail(recipientEmail)
      .then((items: Message[]) => {
        setMessages(items);
      })
      .finally(() => setLoading(false));
  }, [user]);

  const unreadMessages = useMemo(
    () => messages.filter(item => getMessageValue(item, messageFieldIds.status) === "Unread"),
    [messages]
  );

  useEffect(() => {
    if (!unreadMessages.length) {
      dispatch(setMessagesUnread(false));
      return;
    }

    dispatch(setMessagesUnread(true));

    Promise.all(unreadMessages
      .filter(item => item.id)
      .map((item) => airtable.messages.update(item.id!, {
        [messageFieldIds.status]: "Read",
        [messageFieldIds.readAt]: new Date().toISOString()
      })))
      .then(() => {
        setMessages((current) => current.map((item) => {
          if (item[messageFieldIds.status] !== "Unread") return item;
          return {
            ...item,
            [messageFieldIds.status]: "Read",
            [messageFieldIds.readAt]: new Date().toISOString()
          };
        }));
        dispatch(setMessagesUnread(false));
      })
      .catch(() => {
        // Keep UI functional even if one update fails.
      });
  }, [dispatch, unreadMessages]);

  return (
    <main className="dashboard-page page bg-white">
      <div className="max-w-5xl mx-auto py-12 px-4">
        <h1 className="text-3xl font-semibold text-gray-800 mb-2">Messages</h1>
        <p className="text-gray-500 mb-8">Messages from DWF staff relevant to your account.</p>

        {loading ? (
          <p className="text-gray-500">Loading messages...</p>
        ) : null}

        {!loading && !messages.length ? (
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-6 text-gray-600">
            You do not have any messages right now.
          </div>
        ) : null}

        <div className="space-y-4">
          {messages.map((message) => {
            const sentAt =
              getMessageValue(message, messageFieldIds.sentAt) ||
              getMessageValue(message, messageFieldIds.liveDate);
            const requiresAction = !!getMessageValue(
              message,
              messageFieldIds.requiresAction
            );
            const actionUrl = getMessageValue(message, messageFieldIds.actionUrl);
            const actionUrlText = getMessageValue(message, messageFieldIds.actionUrlText);
            const isArchived =
              getMessageValue(message, messageFieldIds.status) === "Archived";
            const subject = getMessageValue(message, messageFieldIds.subject);
            const body = getMessageValue(message, messageFieldIds.body);

            return (
              <article
                key={message.id}
                className={`rounded-xl border p-6 ${isArchived ? "bg-gray-50 border-gray-200" : "bg-white border-purple-100"}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h2 className="text-xl font-semibold text-gray-800">
                    {subject || "Untitled message"}
                  </h2>
                  {sentAt ? (
                    <span className="text-sm text-gray-500">{getNiceFormatDateTimeString(new Date(sentAt))}</span>
                  ) : null}
                </div>

                <p className="text-gray-700 whitespace-pre-wrap">{body}</p>

                {requiresAction && actionUrl ? (
                  <a
                    href={actionUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex mt-4 rounded-md bg-[#4E2873] px-4 py-2 text-white text-sm font-medium hover:bg-[#5B3186]"
                  >
                    {actionUrlText || "Open action link"}
                  </a>
                ) : null}
              </article>
            )
          })}
        </div>
      </div>
    </main>
  )
}
